import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FiMessageSquare } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { Row, Col, Form, Spinner } from "react-bootstrap";
import {
  FiSearch,
  FiUpload,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiX,
  FiArrowRight,
  FiLayers,
  FiBox,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { useTheme } from "../../../contexts/ThemeContext";
import MyContext from "../../../contexts/AppContext";
import SellerMasterLayout from "../../../layouts/seller/SellerMasterLayout";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/seller/screens/SellerProductManagement.css";

const ProductsManagement = () => {
  const { isDark } = useTheme();
  const { baseUrl, baseUrlForFiles, sellerAuthToken } = useContext(MyContext);
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productId, setProductId] = useState();
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [category, setCategory] = useState("");
  const [conversations, setConversations] = useState([]);
  const [description, setDescription] = useState("");
  const [products, setProducts] = useState([]);
  const [isCreateUpdateLoading, setIsCreateUpdateLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [subCategory, setSubCategory] = useState("");
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);

  // ── Pagination (backend se aata hai) ──
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // ── Sizes ──
  const [sizes, setSizes] = useState([]);
  const [variantSize, setVariantSize] = useState("");

  // ── Colors — now a fully independent chip list, same pattern as sizes.
  // No link to any specific image; saved only to chproductvariants.colors. ──
  const [colors, setColors] = useState([]);
  const [pendingColor, setPendingColor] = useState("#000000");

  // ── Images — plain file picker, multiple at once, no color attached. ──
  // newImages = images picked in this session, not yet uploaded: [{ file, preview }]
  const [newImages, setNewImages] = useState([]);
  // existingImages = images already saved on the product (edit mode): [{ id, image_path }]
  const [existingImages, setExistingImages] = useState([]);
  // ids of existing images the user removed in this edit session — sent to backend on update
  const [removedImageIds, setRemovedImageIds] = useState([]);

  const [shopGuardModal, setShopGuardModal] = useState(false);
  const [shopGuardType, setShopGuardType] = useState("");
  const [shopStatusLoaded, setShopStatusLoaded] = useState(null);
  const [statsData, setStatsData] = useState(null);

  const checkShop = () => {
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    if (!seller?.id) return;
    const formdata = new FormData();
    formdata.append("seller_id", seller.id);
    axios
      .post(`${baseUrl}/seller/store/getmystore`, formdata,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
)
      .then((res) => {
        const store = res.data.store;
        if (!store?.store_name) {
          setShopStatusLoaded("no-shop");
        } else {
          setShopStatusLoaded(store.status);
        }
      })
      .catch((err) => {
        console.log("checkShop error:", err.response?.data || err.message);
      });
  };

  const toUtcDate = (dateString) => {
    if (!dateString) return null;
    return new Date(
      dateString.includes("T") ? dateString : dateString + " UTC",
    );
  };

  const isUnread = (item) => {
    if (!item.latest_message || item.latest_message.sender !== "chcustomer") {
      return false;
    }
    if (!item.seller_read_at) {
      return true;
    }
    return (
      toUtcDate(item.latest_message.created_at) > toUtcDate(item.seller_read_at)
    );
  };

  const hasUnreadForProduct = (productId) => {
    return conversations.some(
      (item) => item.product?.id === productId && isUnread(item),
    );
  };

  const viewConversations = () => {
    const loggedInSeller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const sellerId = loggedInSeller?.id;

    axios
      .post(
        `${baseUrl}/seller/conversation/getAll`,
        { seller_id: sellerId },
        {
          headers: {
            Authorization: `Bearer ${sellerAuthToken}`,
          },
        },
      )
      .then((response) => {
        setConversations(response.data.conversations);
      })
      .catch(() => {});
  };

  const resetForm = () => {
    setEditingProduct(null);
    setProductId(null);
    setName("");
    setPrice("");
    setStock("");
    setCategory("");
    setDescription("");
    setSizes([]);
    setVariantSize("");
    setColors([]);
    setPendingColor("#000000");
    setNewImages([]);
    setExistingImages([]);
    setRemovedImageIds([]);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleCreateClick = () => {
    if (shopStatusLoaded === "no-shop") {
      setShopGuardType("no-shop");
      setShopGuardModal(true);
      return;
    }
    if (shopStatusLoaded === "Pending") {
      setShopGuardType("pending");
      setShopGuardModal(true);
      return;
    }
    resetForm();
    setShowModal(true);
  };

  // ── Images: plain multi-file picker, no color attached ──
  const handleImageFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setNewImages([...newImages, ...mapped]);
    e.target.value = ""; // allow re-selecting the same file again later
  };

  const removeNewImage = (index) =>
    setNewImages(newImages.filter((_, i) => i !== index));

  // Removes an already-saved image from the edit view and queues its id
  // for deletion on the backend when "Update Product" is confirmed.
  const removeExistingImage = (img) => {
    setExistingImages(existingImages.filter((i) => i.id !== img.id));
    setRemovedImageIds([...removedImageIds, img.id]);
  };

  const addSize = () => {
    if (!variantSize.trim()) {
      toast.warning("Please enter a size.");
      return;
    }
    const incoming = variantSize
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "");
    const duplicates = incoming.filter((s) => sizes.includes(s));
    const newSizes = incoming.filter((s) => !sizes.includes(s));
    if (duplicates.length > 0)
      toast.warning(`Already exists: ${duplicates.join(", ")}`);
    if (newSizes.length > 0) setSizes([...sizes, ...newSizes]);
    setVariantSize("");
  };

  const removeSize = (index) => setSizes(sizes.filter((_, i) => i !== index));

  // ── Colors: independent chip list, same pattern as sizes ──
  const addColor = () => {
    if (colors.includes(pendingColor)) {
      toast.warning("This color is already added.");
      return;
    }
    setColors([...colors, pendingColor]);
  };

  const removeColor = (index) => setColors(colors.filter((_, i) => i !== index));

  // Colors + sizes both go to the variant endpoint (comma separated)
  const saveVariants = (pid, currentSizes, currentColors) => {
    if (currentSizes.length === 0 && currentColors.length === 0) return;
    axios
      .post(
        `${baseUrl}/seller/product/createVariant`,
        {
          product_id: pid,
          sizes: currentSizes.join(","),
          colors: currentColors.join(","),
        },
        {
          headers: {
            Authorization: `Bearer ${sellerAuthToken}`,
          },
        },
      )
      .catch(() => {});
  };

  // Only image files go to product_image[] — no per-image color anymore
  const buildImageFormData = (formdata) => {
    newImages.forEach((entry) => {
      formdata.append("product_image[]", entry.file);
    });
  };

  const updateProduct = async () => {
    setShowModal(false);
    const result = await Swal.fire({
      title: "Save Changes?",
      text: "Do you want to update this product?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Update!",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) {
      setShowModal(true);
      return;
    }

    const currentSizes = [...sizes];
    const currentColors = [...colors];
    const currentProductId = productId;

    setIsCreateUpdateLoading(true);
    const formdata = new FormData();
    formdata.append("id", productId);
    formdata.append("product_name", name);
    formdata.append("description", description);
    formdata.append("price", price);
    formdata.append("stock_quantity", stock);
    formdata.append("category_id", category);
    formdata.append("subcategory_id", subCategory);

    // tell the backend which existing images to delete
    removedImageIds.forEach((id) => {
      formdata.append("removed_image_ids[]", id);
    });

    buildImageFormData(formdata);

    axios
      .post(`${baseUrl}/seller/product/updateProduct`, formdata,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },)
      .then(() => {
        saveVariants(currentProductId, currentSizes, currentColors);
        viewAllProduct();
        handleCloseModal();
        setIsCreateUpdateLoading(false);
        toast.success("Product updated successfully!");
      })
      .catch(() => {
        setIsCreateUpdateLoading(false);
        setShowModal(true);
        toast.error("Update failed. Please try again.");
      });
  };

  const createProduct = () => {
    setIsCreateUpdateLoading(true);
    const loggedInSeller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const sellerId = loggedInSeller?.id;
    const currentSizes = [...sizes];
    const currentColors = [...colors];

    const formdata = new FormData();
    formdata.append("seller_id", sellerId);
    formdata.append("product_name", name);
    formdata.append("description", description);
    formdata.append("price", price);
    formdata.append("stock_quantity", stock);
    formdata.append("category_id", category);
    formdata.append("subcategory_id", subCategory);
    formdata.append("status", "Pending");
    buildImageFormData(formdata);

    axios
      .post(`${baseUrl}/seller/product/createProduct`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then((response) => {
        const newProductId = response.data.product_id;
        if (
          (currentSizes.length > 0 || currentColors.length > 0) &&
          newProductId
        ) {
          saveVariants(newProductId, currentSizes, currentColors);
        }
        viewAllProduct();
        handleCloseModal();
        setIsCreateUpdateLoading(false);
        toast.success("Product created successfully!");
      })
      .catch(() => {
        setIsCreateUpdateLoading(false);
        toast.error("Failed to create product. Try again.");
      });
  };

  // Pagination-aware fetch — currentPage bhi backend ko bhejta hai
  const viewAllProduct = () => {
    setLoading(true);
    const loggedInSeller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const sellerId = loggedInSeller?.id;
    axios
      .post(
        `${baseUrl}/seller/product/viewAllProduct`,
        {
          seller_id: sellerId,
          page: currentPage,
        },
        {
          headers: {
            Authorization: `Bearer ${sellerAuthToken}`,
          },
        },
      )
      .then((response) => {
        setProducts(response.data.products);
        setTotalPages(response.data.last_page);
        setTotalItems(response.data.total);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const viewAllCategories = () => {
    axios
      .get(`${baseUrl}/seller/categories/getcategories`,{
        headers: { Authorization: `Bearer ${sellerAuthToken}` },
      })
      .then((response) => setCategories(response.data.categories))
      .catch((error) => console.log(error));
  };

  const deleteProduct = async (id) => {
    const result = await Swal.fire({
      title: "Delete Product?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete!",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    const formdata = new FormData();
    formdata.append("id", id);
    axios
      .post(`${baseUrl}/seller/product/deleteProduct`, formdata,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },)
      .then(() => {
        viewAllProduct();
        toast.success("Product deleted.");
      })
      .catch(() => toast.error("Could not delete. Try again."));
  };

  const getStats = () => {
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const fd = new FormData();
    fd.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/product/stats`, fd,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },)
      .then((res) => setStatsData(res.data))
      .catch(() => {});
  };

  // currentPage jab bhi change ho, backend se woh page mangwao
  useEffect(() => {
    viewAllProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    viewAllCategories();
    checkShop();
    getStats();
    viewConversations();
  }, []);

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.product_name
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "All Status" || product.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Max 5 page-number buttons visible
  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    const pages = [];
    for (let page = startPage; page <= endPage; page++) pages.push(page);
    return pages;
  };

  const productForm = (
    <div className="pm-form">
      <Form.Group className="pm-form-group">
        <Form.Label className="pm-form-label">Product Name</Form.Label>
        <Form.Control
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Crystal Ring Set"
          className="pm-form-input"
        />
      </Form.Group>

      <Form.Group className="pm-form-group">
        <Form.Label className="pm-form-label">Description</Form.Label>
        <Form.Control
          as="textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter product description..."
          className="pm-form-input"
        />
      </Form.Group>

      <Row>
        <Col sm={6}>
          <Form.Group className="pm-form-group">
            <Form.Label className="pm-form-label">Price</Form.Label>
            <Form.Control
              type="text"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="e.g. 1000"
              className="pm-form-input"
              min="0"
            />
          </Form.Group>
        </Col>
        <Col sm={6}>
          <Form.Group className="pm-form-group">
            <Form.Label className="pm-form-label">Stock</Form.Label>
            <Form.Control
              type="text"
              value={stock}
              onChange={(e) => setStock(e.target.value)}
              placeholder="120"
              className="pm-form-input"
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="pm-form-group">
        <Form.Label className="pm-form-label">Category</Form.Label>
        <Form.Select
          value={category}
          className="pm-form-input"
          onChange={(e) => {
            const selectedCategoryId = e.target.value;
            setCategory(selectedCategoryId);
            const selectedCat = categories.find(
              (cat) => cat.id == selectedCategoryId,
            );
            setFilteredSubCategories(selectedCat?.subcategories || []);
            setSubCategory("");
          }}
        >
          <option value="">Choose category</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.category_name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="pm-form-group">
        <Form.Label className="pm-form-label">Sub Category</Form.Label>
        <Form.Select
          value={subCategory}
          onChange={(e) => setSubCategory(e.target.value)}
          className="pm-form-input"
          disabled={!category}
        >
          <option value="">Choose subcategory</option>
          {filteredSubCategories.map((sub) => (
            <option key={sub.id} value={sub.id}>
              {sub.subcategoryname}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="pm-form-group">
        <Form.Label className="pm-form-label">Size</Form.Label>
        <div className="pm-size-field-row">
          <Form.Control
            type="text"
            placeholder="e.g. S, M, L, XL (comma separated)"
            value={variantSize}
            onChange={(e) => setVariantSize(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSize()}
            className="pm-form-input pm-variant-size-input"
          />
          <button
            type="button"
            className="pm-add-variant-btn"
            onClick={addSize}
            title="Add Size"
          >
            <FiPlus size={16} />
          </button>
        </div>
        {sizes.length > 0 && (
          <div className="pm-variants-list">
            {sizes.map((s, i) => (
              <div key={i} className="pm-variant-tag">
                <span className="pm-variant-size-label">{s}</span>
                <button
                  type="button"
                  className="pm-remove-variant-btn"
                  onClick={() => removeSize(i)}
                >
                  <FiX size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Form.Group>

      {/* Colors — independent chip list, same pattern as Size, no image link */}
      <Form.Group className="pm-form-group">
        <Form.Label className="pm-form-label">Colors</Form.Label>
        <div className="pm-color-field-row">
          <div className="pm-color-picker-wrapper">
            <input
              type="color"
              value={pendingColor}
              onChange={(e) => setPendingColor(e.target.value)}
              className="pm-color-input"
              title="Pick a color"
            />
            <div
              className="pm-color-preview"
              style={{ backgroundColor: pendingColor }}
            />
          </div>
          <span className="pm-color-hex-label">{pendingColor}</span>
          <button
            type="button"
            className="pm-add-variant-btn"
            onClick={addColor}
            title="Add Color"
          >
            <FiPlus size={16} />
          </button>
        </div>
        {colors.length > 0 && (
          <div className="pm-variants-list">
            {colors.map((c, i) => (
              <div key={i} className="pm-variant-tag">
                <span
                  className="pm-variant-color-dot"
                  style={{ backgroundColor: c }}
                />
                <span className="pm-variant-size-label">{c}</span>
                <button
                  type="button"
                  className="pm-remove-variant-btn"
                  onClick={() => removeColor(i)}
                >
                  <FiX size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Form.Group>

      {/* Images — plain multi-file picker, no color attached */}
      <Form.Group className="pm-form-group">
        <Form.Label className="pm-form-label">Product Images</Form.Label>

        <div className="pm-file-input-wrapper">
          <label htmlFor="product-image" className="pm-file-label">
            <FiUpload size={16} />
            <span>Choose Images</span>
          </label>
          <Form.Control
            type="file"
            id="product-image"
            accept="image/*"
            multiple
            onChange={handleImageFilesChange}
            className="pm-file-input"
          />
        </div>

        {/* Existing images already saved on the product (edit mode) */}
        {existingImages.length > 0 && (
          <>
            <span
              style={{
                fontSize: "0.75rem",
                color: "#6b7280",
                display: "block",
                marginTop: "10px",
              }}
            >
              Current images
            </span>
            <div className="pm-variants-list">
              {existingImages.map((img) => (
                <div key={`existing-${img.id}`} className="pm-variant-tag">
                  <img
                    src={`${baseUrlForFiles}/${img.image_path}`}
                    alt="existing"
                    style={{
                      width: "28px",
                      height: "28px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "2px solid #e5e7eb",
                    }}
                  />
                  <button
                    type="button"
                    className="pm-remove-variant-btn"
                    onClick={() => removeExistingImage(img)}
                    title="Remove image"
                  >
                    <FiX size={11} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Newly picked images, not yet saved */}
        {newImages.length > 0 && (
          <>
            <span
              style={{
                fontSize: "0.75rem",
                color: "#6b7280",
                display: "block",
                marginTop: "10px",
              }}
            >
              New images to upload
            </span>
            <div className="pm-variants-list">
              {newImages.map((entry, i) => (
                <div key={`new-${i}`} className="pm-variant-tag">
                  <img
                    src={entry.preview}
                    alt="new"
                    style={{
                      width: "28px",
                      height: "28px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "2px solid #e5e7eb",
                    }}
                  />
                  <button
                    type="button"
                    className="pm-remove-variant-btn"
                    onClick={() => removeNewImage(i)}
                  >
                    <FiX size={11} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </Form.Group>
    </div>
  );

  return (
    <SellerMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className={`pm-shell ${isDark ? "dark-mode" : ""}`}>
        <main className="pm-main">
          <section className="pm-card-shell">
            <div className="pm-stats">
              <div className="pm-stat-card">
                <div>
                  <span className="pm-stat-label">Total Products</span>
                  <h3 className="pm-stat-value">
                    {statsData?.total_products ?? "0"}
                  </h3>
                  <p className="pm-stat-sub">All listed products</p>
                </div>
                <div className="pm-stat-icon">
                  <FiLayers />
                </div>
              </div>
              <div className="pm-stat-card">
                <div>
                  <span className="pm-stat-label">Approved</span>
                  <h3 className="pm-stat-value">
                    {statsData?.approved_products ?? "0"}
                  </h3>
                  <p className="pm-stat-sub">Live on store</p>
                </div>
                <div className="pm-stat-icon">
                  <FiBox />
                </div>
              </div>
              <div className="pm-stat-card">
                <div>
                  <span className="pm-stat-label">Rejected</span>
                  <h3 className="pm-stat-value">
                    {statsData?.rejected_products ?? "0"}
                  </h3>
                  <p className="pm-stat-sub">Needs attention</p>
                </div>
                <div className="pm-stat-icon pm-stat-icon--alert">
                  <FiAlertCircle />
                </div>
              </div>
            </div>

            <div className="pm-header">
              <div className="pm-header-left" />
              <div className="pm-header-filters">
                <div className="pm-search">
                  <FiSearch />
                  <input
                    placeholder="Search products"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <select
                  className="pm-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option>All Status</option>
                  <option>Approved</option>
                  <option>Rejected</option>
                  <option>Pending</option>
                </select>
                <button className="pm-btn-add" onClick={handleCreateClick}>
                  <FiPlus size={14} /> Add Product
                </button>
              </div>
            </div>

            <div className="pm-table-wrapper">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Images</th>
                    <th>Product</th>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Sub Category</th>
                    <th>Colors</th>
                    <th>Sizes</th>
                    <th>Stock</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="12" className="text-center py-4">
                        <Spinner
                          animation="border"
                          size="sm"
                          className="pm-spinner"
                        />
                        <span className="ms-2 pm-muted pm-loading-text">
                          Loading...
                        </span>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    filteredProducts.length > 0 &&
                    filteredProducts.map((product) => (
                      <tr key={product.id}>
                        <td>{product.id}</td>
                        <td>
                          {product.images && product.images.length > 0 ? (
                            <div className="pm-table-color-dots">
                              {product.images.map((img, i) => (
                                <img
                                  key={i}
                                  src={`${baseUrlForFiles}/${img.image_path}`}
                                  alt={product.product_name}
                                  className="pm-product-img"
                                />
                              ))}
                            </div>
                          ) : (
                            <span className="pm-muted">No Image</span>
                          )}
                        </td>
                        <td>
                          <p className="pm-cell-title">
                            {product.product_name}
                          </p>
                        </td>
                        <td>
                          <div className="pm-desc-box">
                            {product.description || "—"}
                          </div>
                        </td>
                        <td>{product.category?.category_name ?? "—"}</td>
                        <td>{product.subcategory?.subcategoryname ?? "—"}</td>
                        <td>
                          {/* Colors come from the variant record — a standalone product attribute */}
                          {product.variant?.colors ? (
                            <div className="pm-table-color-dots">
                              {product.variant.colors
                                .split(",")
                                .filter(Boolean)
                                .map((c, i) => (
                                  <span
                                    key={i}
                                    title={c}
                                    style={{
                                      backgroundColor: c,
                                      display: "inline-block",
                                      width: "16px",
                                      height: "16px",
                                      borderRadius: "50%",
                                      marginRight: "4px",
                                      border: "1px solid #e5e7eb",
                                    }}
                                  />
                                ))}
                            </div>
                          ) : (
                            <span className="pm-muted">—</span>
                          )}
                        </td>
                        <td>
                          {product.variant?.sizes ? (
                            <div className="pm-table-sizes">
                              {product.variant.sizes
                                .split(",")
                                .filter(Boolean)
                                .map((s, i) => (
                                  <span key={i} className="pm-table-size-badge">
                                    {s}
                                  </span>
                                ))}
                            </div>
                          ) : (
                            <span className="pm-muted">—</span>
                          )}
                        </td>
                        <td>{product.stock_quantity}</td>
                        <td>Rs {product.price}</td>
                        <td>
                          <span
                            className={`pm-status ${
                              product.status?.toLowerCase() === "approved"
                                ? "pm-status--active"
                                : product.status?.toLowerCase() === "rejected"
                                  ? "pm-status--danger"
                                  : "pm-status--warning"
                            }`}
                          >
                            {product.status}
                          </span>
                        </td>
                        <td>
                          <div className="pm-action-btns">
                            <button
                              className="pm-btn-edit"
                              onClick={() => {
                                setEditingProduct(product);
                                setProductId(product.id);
                                setName(product.product_name);
                                setPrice(product.price);
                                setStock(product.stock_quantity);
                                setCategory(product.category_id);
                                const selectedCat = categories.find(
                                  (cat) => cat.id == product.category_id,
                                );
                                setFilteredSubCategories(
                                  selectedCat?.subcategories || [],
                                );
                                setSubCategory(product.subcategory_id || "");
                                setDescription(product.description || "");
                                setNewImages([]);
                                setRemovedImageIds([]);

                                // Images loaded plainly — no color attached
                                setExistingImages(
                                  (product.images || []).map((img) => ({
                                    id: img.id,
                                    image_path: img.image_path,
                                  })),
                                );

                                setSizes(
                                  product.variant?.sizes
                                    ? product.variant.sizes
                                        .split(",")
                                        .filter(Boolean)
                                    : [],
                                );

                                // Colors loaded directly from the variant record
                                setColors(
                                  product.variant?.colors
                                    ? product.variant.colors
                                        .split(",")
                                        .filter(Boolean)
                                    : [],
                                );

                                setShowModal(true);
                              }}
                            >
                              <FiEdit2 size={12} /> Edit
                            </button>
                            <button
                              className="pm-btn-delete"
                              onClick={() => deleteProduct(product.id)}
                            >
                              <FiTrash2 size={12} /> Delete
                            </button>
                            <button
                              className="pm-btn-messages"
                              onClick={() =>
                                navigate("/seller/messages", {
                                  state: { productId: product.id },
                                })
                              }
                            >
                              <FiMessageSquare size={12} /> Messages
                              {hasUnreadForProduct(product.id) && (
                                <span className="pm-messages-unread-dot" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                  {!loading && filteredProducts.length === 0 && (
                    <tr>
                      <td colSpan="12" className="text-center py-5">
                        <div className="pm-empty-icon">📦</div>
                        <p className="pm-muted mt-2">No products found</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination controls */}
              {!loading && filteredProducts.length > 0 && totalPages > 1 && (
                <div className="pm-pagination-bar">
                  <span className="pm-pagination-info">
                    Showing {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                  </span>
                  <div className="pm-pagination-controls">
                    <button
                      className="pm-page-btn pm-page-btn--nav"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <FiChevronLeft size={14} />
                    </button>
                    {getVisiblePageNumbers().map((page) => (
                      <button
                        key={page}
                        className={`pm-page-btn ${page === currentPage ? "pm-page-btn--active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="pm-page-btn pm-page-btn--nav"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <FiChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>

      {showModal && (
        <div className="pm-modal-overlay" onClick={handleCloseModal}>
          <div
            className="pm-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pm-modal-header">
              <h4>{editingProduct ? "Edit Product" : "Add New Product"}</h4>
              <button className="pm-modal-close" onClick={handleCloseModal}>
                <FiX size={20} />
              </button>
            </div>

            <div className="pm-modal-body">{productForm}</div>

            <div className="pm-modal-footer">
              <button
                className="pm-btn-cancel"
                onClick={handleCloseModal}
                disabled={isCreateUpdateLoading}
              >
                Cancel
              </button>
              <button
                className="pm-btn-save"
                onClick={editingProduct ? updateProduct : createProduct}
                disabled={isCreateUpdateLoading}
              >
                {isCreateUpdateLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingProduct ? "Updating..." : "Creating..."}
                  </>
                ) : editingProduct ? (
                  "Update Product"
                ) : (
                  "+ Add Product"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {shopGuardModal && (
        <div
          className="pm-guard-overlay"
          onClick={() => setShopGuardModal(false)}
        >
          <div className="pm-guard-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-guard-icon-wrap">
              <div className="pm-guard-ring pm-guard-ring--1" />
              <div className="pm-guard-ring pm-guard-ring--2" />
              <div
                className={`pm-guard-icon-circle ${shopGuardType === "pending" ? "pm-guard-icon-circle--amber" : ""}`}
              >
                <span className="pm-guard-emoji">
                  {shopGuardType === "pending" ? "⏳" : "🏪"}
                </span>
              </div>
            </div>
            <button
              className="pm-guard-close"
              onClick={() => setShopGuardModal(false)}
            >
              <FiX size={15} />
            </button>
            <h2 className="pm-guard-title">
              {shopGuardType === "pending"
                ? "Shop Awaiting Approval"
                : "Set Up Your Shop First"}
            </h2>
            <p className="pm-guard-text">
              {shopGuardType === "pending"
                ? "Your shop is currently under review by our admin team. You'll be able to add products as soon as your shop is approved."
                : "You haven't created your shop yet. Please set up your shop profile before adding products."}
            </p>
            {shopGuardType === "no-shop" && (
              <button
                className="pm-guard-btn-confirm"
                onClick={() => {
                  setShopGuardModal(false);
                  navigate("/seller/editstore");
                }}
              >
                Create My Shop <FiArrowRight size={14} />
              </button>
            )}
            <button
              className="pm-guard-btn-cancel"
              onClick={() => setShopGuardModal(false)}
            >
              {shopGuardType === "pending" ? "Got it" : "Maybe later"}
            </button>
          </div>
        </div>
      )}
    </SellerMasterLayout>
  );
};

export default ProductsManagement;